// A boat's owners and invites to join it (TOM-458, ADR 0319, api_contracts
// §5.19). Every owner is equal: any owner invites, revokes an invite, removes
// an owner, or leaves; the last owner cannot leave and deletes the boat
// instead. These are the routes' shapes and the sentences their refusals show.

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api';

export interface BoatOwner {
  userId: string;
  displayName: string | null;
  email: string;
  grantedAt: string;
  you: boolean;
}

export interface OpenInvite {
  id: string;
  email: string;
  invitedBy: { userId: string; displayName: string | null } | null;
  createdAt: string;
  expiresAt: string;
}

export interface BoatOwners {
  owners: BoatOwner[];
  invites: OpenInvite[];
}

export type InviteState = 'open' | 'accepted' | 'declined' | 'revoked' | 'expired';

export interface InvitePreview {
  inviteId: string;
  boatName: string | null;
  invitedBy: string | null;
  email: string;
  state: InviteState;
  expiresAt: string;
}

/** What a refusal from the owner and invite routes says to the person. */
const REFUSALS: Record<string, string> = {
  invalid_email: 'Enter an email address.',
  already_owner: 'That address already owns this boat.',
  invite_open: 'That address already has an invite. Revoke it to send a new one.',
  owner_limit: 'A boat can have 10 owners and open invites at most.',
  rate_limited: 'Too many invites for now. Try again in an hour.',
  last_owner: 'You are the only owner. Delete the boat instead.',
  owner_not_found: 'That person is no longer an owner.',
  invite_not_found: 'That invite is no longer open.',
  boat_has_other_owners: 'Other owners share this boat. Leave it instead.',
  boat_offline: 'The boat is offline. It keeps its own units until it is back online.',
};

/** The sentence for a failed owner or invite request. */
export function ownersErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { body?: { error?: unknown } }).body?.error;
  if (typeof code === 'string' && code in REFUSALS) return REFUSALS[code]!;
  return fallback;
}

/** "Sam (sam@example.com)", or the address when there is no name. */
export function ownerLabel(owner: { displayName: string | null; email: string }): string {
  const name = owner.displayName?.trim();
  return name ? `${name} (${owner.email})` : owner.email;
}

/** The invite page's sentence for an invite that can no longer be accepted. */
export function closedInviteMessage(state: Exclude<InviteState, 'open'>): string {
  switch (state) {
    case 'accepted':
      return 'This invite has already been accepted.';
    case 'declined':
      return 'This invite was declined.';
    case 'revoked':
      return 'This invite was withdrawn. Ask an owner of the boat to send a new one.';
    case 'expired':
      return 'This invite has expired. Ask an owner of the boat to send a new one.';
  }
}

export function inviteOwner(boatId: string, email: string): Promise<{ invite: OpenInvite }> {
  return api(`/v1/boats/${boatId}/invites`, {
    method: 'POST',
    body: { email },
    idempotencyKey: crypto.randomUUID(),
  });
}

export function revokeInvite(boatId: string, inviteId: string): Promise<void> {
  return api(`/v1/boats/${boatId}/invites/${inviteId}`, {
    method: 'DELETE',
    idempotencyKey: crypto.randomUUID(),
  });
}

/** Remove an owner; your own userId is leaving. */
export function removeOwner(boatId: string, userId: string): Promise<void> {
  return api(`/v1/boats/${boatId}/owners/${userId}`, {
    method: 'DELETE',
    idempotencyKey: crypto.randomUUID(),
  });
}

export function sendDisplayUnits(boatId: string): Promise<{ delivered: number; staged: boolean }> {
  return api(`/v1/boats/${boatId}/display-units`, {
    method: 'POST',
    idempotencyKey: crypto.randomUUID(),
  });
}

function invitePath(inviteId: string): string {
  return `/v1/invites/${encodeURIComponent(inviteId)}`;
}

export function previewInvite(inviteId: string, token: string): Promise<InvitePreview> {
  return api(`${invitePath(inviteId)}?token=${encodeURIComponent(token)}`);
}

export function acceptInvite(inviteId: string, token: string): Promise<{ boatId: string }> {
  return api(`${invitePath(inviteId)}/accept`, { method: 'POST', body: { token } });
}

export function declineInvite(inviteId: string, token: string): Promise<void> {
  return api(`${invitePath(inviteId)}/decline`, { method: 'POST', body: { token } });
}

/**
 * The owners read, or null when the body is not that shape. A reply that is not
 * one (a proxy's error page, a mocked `{}`) must show as a failed load, never
 * reach the panel and take the settings page down with it.
 */
export function parseBoatOwners(body: unknown): BoatOwners | null {
  if (typeof body !== 'object' || body === null) return null;
  const { owners, invites } = body as { owners?: unknown; invites?: unknown };
  if (!Array.isArray(owners) || !Array.isArray(invites)) return null;
  return { owners: owners as BoatOwner[], invites: invites as OpenInvite[] };
}

export type OwnersState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; owners: BoatOwner[]; invites: OpenInvite[] };

/** The boat's owners and open invites, and a way to read them again. */
export function useBoatOwners(boatId: string | null): {
  state: OwnersState;
  refresh: () => void;
} {
  const [state, setState] = useState<OwnersState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // No boat id, no read: a Try it guest is refused the owners list (TOM-868).
    if (boatId === null) return;
    let live = true;
    api<unknown>(`/v1/boats/${boatId}/owners`)
      .then((body) => {
        if (!live) return;
        const read = parseBoatOwners(body);
        setState(read === null ? { kind: 'error' } : { kind: 'ready', ...read });
      })
      .catch(() => {
        if (live) setState({ kind: 'error' });
      });
    return () => {
      live = false;
    };
  }, [boatId, tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { state, refresh };
}
